import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SITE } from "../lib/data";

const links = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#E5DFD3] py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="group flex flex-col leading-none">
            <span className="font-serif-lux text-2xl md:text-[1.7rem] tracking-tight text-[#1A1A1A]">
              Sambhav K. Pandey
            </span>
            <span className="overline text-[#C19A6B] mt-1">Real Estate · Noida & Goa</span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-sans-lux text-sm tracking-wide link-sweep ${
                    isActive ? "text-[#1A1A1A]" : "text-[#5C5C5C] hover:text-[#1A1A1A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="btn-lux border border-[#1A1A1A] text-[#1A1A1A] hover:text-[#FDFBF7] px-6 py-3 text-xs overline"
            >
              Book a Site Visit
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#1A1A1A]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#FDFBF7] pt-28 px-8 md:hidden"
          >
            <div className="flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <NavLink
                    to={l.to}
                    className="font-serif-lux text-4xl text-[#1A1A1A]"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                className="mt-6 bg-[#1A1A1A] text-[#FDFBF7] px-6 py-4 text-center overline"
              >
                Book a Site Visit
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
